import type {
	AccessControlConditions,
	AccsDefaultParams,
} from "@lit-protocol/types";

// Condition type for EVM basic access control condition
// Must be specified as evmBasic if used as unified access control condition.
export const EVM_BASIC_CONDITION_TYPE = "evmBasic";

export const OR_CONDITION = { operator: "or" as const };

export const AND_CONDITION = { operator: "and" as const };

/**
 * Creates an access control condition that validates the current action's IPFS hash
 * matches the expected hash.
 * @param ipfsHash - The expected IPFS hash of the lit action
 * @param conditionType - The type of condition to create. Must be specified as evmBasic if used as unified access control condition.
 * @returns Access control condition that validates the IPFS hash
 */
export function getLitActionAccessControlCondition(
	ipfsHash: string,
	conditionType: string | undefined = undefined,
): AccsDefaultParams {
	return {
		conditionType,
		contractAddress: "",
		standardContractType: "",
		chain: "ethereum",
		method: "",
		parameters: [":currentActionIpfsId"],
		returnValueTest: {
			comparator: "=",
			value: ipfsHash,
		},
	};
}

/**
 * Creates an access control condition that restricts decryption to a specific PKP address.
 *
 * Uses `:userAddress` parameter which Lit resolves to the address of the PKP
 * that is attempting to decrypt the data. This ensures that only the specified
 * PKP can decrypt the encrypted data.
 *
 * @param pkpAddress - The Ethereum address of the PKP that should have access
 * @param conditionType - The type of condition to create. Must be specified as evmBasic if used as unified access control condition.
 * @returns Access control condition that validates the PKP address matches
 * @throws Error if pkpAddress is not a valid Ethereum address
 */
export function getPkpAccessControlCondition(
	pkpAddress: string,
	conditionType: string | undefined = undefined,
): AccsDefaultParams {
	return {
		conditionType,
		contractAddress: "",
		standardContractType: "",
		chain: "ethereum",
		method: "",
		parameters: [":userAddress"],
		returnValueTest: {
			comparator: "=",
			value: pkpAddress,
		},
	};
}

/**
 * Creates nested unified access control conditions for PKP and multiple IPFS hashes.
 * This allows the same runner secret to be accessed by multiple Lit Actions.
 * Structure: [PKP condition, "and", [IPFS hash conditions connected by "or"]]
 *
 * @param pkpAddress - The Ethereum address of the PKP that should have access
 * @param ipfsHashes - Array of IPFS hashes that should be allowed to decrypt
 * @param conditionType - The type of condition to create. Must be specified as evmBasic if used as unified access control condition.
 * @returns Nested unified access control conditions array
 */
export function createNestedUnifiedAccessControlConditions(
	pkpAddress: string,
	ipfsHashes: string[],
	conditionType: string | undefined = undefined,
): any[] {
	if (ipfsHashes.length === 0) {
		throw new Error("At least one IPFS hash is required");
	}

	const pkpAccessControlCondition = getPkpAccessControlCondition(
		pkpAddress,
		conditionType,
	);

	// Build nested array with IPFS hash conditions connected by "or" operators
	const nestedConditions: any[] = [];
	for (let i = 0; i < ipfsHashes.length; i++) {
		const ipfsHash = ipfsHashes[i];
		if (!ipfsHash) {
			throw new Error(`IPFS hash at index ${i} is undefined`);
		}
		const litActionCondition = getLitActionAccessControlCondition(
			ipfsHash,
			conditionType,
		);
		nestedConditions.push(litActionCondition);

		// Add "or" operator between conditions (except after the last one)
		if (i < ipfsHashes.length - 1) {
			nestedConditions.push(OR_CONDITION);
		}
	}

	return [pkpAccessControlCondition, AND_CONDITION, nestedConditions];
}

/**
 * Creates access control conditions for the vault.
 *
 * @param pkpAddress
 * @param ipfsHashes
 * @returns
 */
export function getVaultAccessControlConditions(
	pkpAddress: string,
	ipfsHashes: string[],
): AccessControlConditions {
	return createNestedUnifiedAccessControlConditions(
		pkpAddress,
		ipfsHashes,
		EVM_BASIC_CONDITION_TYPE,
	);
}
